import React from "react";

interface SplitPieProps {
  splits: number; // denominator: how many equal portions
  claimed: number; // total portions taken by everyone
  mine: number; // portions taken by the currently-selected user
  size?: number;
}

// A small donut showing an item's split at a glance: white = open, grey =
// claimed by others, black = the selected user's slice. Spokes mark the `splits`
// equal wedges, and the center shows how many slices are yours.
const SplitPie: React.FC<SplitPieProps> = ({
  splits,
  claimed,
  mine,
  size = 30,
}) => {
  const s = Math.max(1, splits);
  const stroke = size * 0.3;
  const r = (size - stroke) / 2; // radius of the ring's centerline
  const c = 2 * Math.PI * r;
  const cx = size / 2;
  const cy = size / 2;

  const greyLen = (Math.min(Math.max(claimed, 0), s) / s) * c;
  const blackLen = (Math.min(Math.max(mine, 0), s) / s) * c;

  const outer = size / 2;
  const inner = size / 2 - stroke;
  const spokes: React.ReactNode[] = [];
  if (s > 1) {
    for (let k = 0; k < s; k++) {
      const a = (k / s) * 2 * Math.PI - Math.PI / 2;
      spokes.push(
        <line
          key={k}
          x1={cx + inner * Math.cos(a)}
          y1={cy + inner * Math.sin(a)}
          x2={cx + outer * Math.cos(a)}
          y2={cy + outer * Math.sin(a)}
          stroke="#d0d0d0"
          strokeWidth={1}
        />
      );
    }
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="split-pie"
    >
      {/* base ring — the open (unclaimed) portion */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#fff" strokeWidth={stroke} />
      {/* claimed by others */}
      {greyLen > 0 && (
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="#9e9e9e"
          strokeWidth={stroke}
          strokeDasharray={`${greyLen} ${c - greyLen}`}
          transform={`rotate(-90 ${cx} ${cy})`}
        />
      )}
      {/* the selected user's slice, drawn over the claimed arc */}
      {blackLen > 0 && (
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="#111"
          strokeWidth={stroke}
          strokeDasharray={`${blackLen} ${c - blackLen}`}
          transform={`rotate(-90 ${cx} ${cy})`}
        />
      )}
      {spokes}
      {/* rim + hole outlines so an all-white donut is still visible */}
      <circle cx={cx} cy={cy} r={outer - 0.5} fill="none" stroke="#c4c4c4" strokeWidth={1} />
      <circle cx={cx} cy={cy} r={inner} fill="none" stroke="#c4c4c4" strokeWidth={1} />
      {mine > 0 && (
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={size * 0.32}
          fontWeight={700}
          fill="#111"
        >
          {mine}
        </text>
      )}
    </svg>
  );
};

export default SplitPie;
